import {
  all,
  call,
  put,
  select,
  takeEvery,
  takeLatest,
} from "redux-saga/effects";
import api from "../../utils/api";
import { libertyDecisionToast } from "../../utils/libertyReviewActions";
import { errorAlert, successAlert } from "../Alerts/actions";
import {
  fetchLibertyReviewSuccess,
  fetchLibertyReviewFailure,
  recordLibertyDecisionSuccess,
  recordLibertyDecisionFailure,
} from "./actions";
import { FETCH_LIBERTY_REVIEW, RECORD_LIBERTY_DECISION } from "./constants";

/** `utils/api` already unwraps axios, so `data` is the API's own envelope. */
const unwrap = (data) => {
  const body = data?.data ?? data ?? {};
  return body && typeof body === "object" && !Array.isArray(body) ? body : {};
};

const isPlainObject = (value) =>
  Boolean(value) && typeof value === "object" && !Array.isArray(value);

export function* fetchLibertyReviewSaga({ payload }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const { locationId } = payload || {};
  try {
    const { data } = yield call(
      api(token).get,
      `/api/governance/liberty-review?location_id=${encodeURIComponent(locationId)}`,
    );
    const body = unwrap(data);
    yield put(
      fetchLibertyReviewSuccess({
        locationId,
        rows: Array.isArray(body?.rows) ? body.rows : [],
        counts: isPlainObject(body?.counts) ? body.counts : null,
        fetchedAt: new Date().toISOString(),
      }),
    );
  } catch (e) {
    yield put(fetchLibertyReviewFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e?.error === "string") yield put(errorAlert(e.error));
    else yield put(errorAlert("Error while loading the liberty review queue."));
  }
}

export function* recordLibertyDecisionSaga({ payload, onSuccess }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const { authorisationUuid, action, note } = payload || {};
  const trimmedNote = typeof note === "string" ? note.trim() : "";
  try {
    const { data } = yield call(
      api(token).post,
      `/api/governance/liberty-review/${authorisationUuid}/decision`,
      trimmedNote ? { action, note: trimmedNote } : { action },
    );
    const body = unwrap(data);
    yield put(
      recordLibertyDecisionSuccess({
        authorisationUuid,
        libertyStatus:
          typeof body?.liberty_status === "string" ? body.liberty_status : null,
        agniReview: isPlainObject(body?.agni_review) ? body.agni_review : null,
        // A returned `agni_review: null` means the review closed — a real
        // change. An omitted key means the server said nothing about it, and
        // the row must keep what it had.
        agniReviewReturned: Object.prototype.hasOwnProperty.call(
          body,
          "agni_review",
        ),
      }),
    );
    yield put(successAlert(libertyDecisionToast(action)));
    if (typeof onSuccess === "function") onSuccess(body);
  } catch (e) {
    yield put(recordLibertyDecisionFailure({ authorisationUuid }));
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e?.error === "string") yield put(errorAlert(e.error));
    else yield put(errorAlert("The review decision could not be recorded."));
  }
}

export function* fetchLibertyReviewFlow() {
  yield takeLatest(FETCH_LIBERTY_REVIEW, fetchLibertyReviewSaga);
}

export function* recordLibertyDecisionFlow() {
  yield takeEvery(RECORD_LIBERTY_DECISION, recordLibertyDecisionSaga);
}

export default function* LibertyReviewSaga() {
  yield all([fetchLibertyReviewFlow(), recordLibertyDecisionFlow()]);
}
